import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccountGroup } from '@vendasta/account-group';
import { BusinessSelectorDialogComponent, BusinessSelectorDialogModule } from '@vendasta/businesses';
import { ConversationChannel, GlobalParticipantType, SubjectParticipant } from '@vendasta/conversation';
import { GalaxyNavLayoutModule } from '@vendasta/galaxy/nav-layout';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyPageNavModule } from '@vendasta/galaxy/page-nav';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { ConversationListService } from '../../../core/src/lib/conversation-list.service';
import { toFirestoreId } from '../../../core/src/lib/conversation-utils';
import { ConversationService } from '../../../core/src/lib/conversation.service';
import { InboxService } from '../../../core/src/lib/inbox.service';
import { ConversationConfig } from '../../../core/src/lib/interface/config.interface';
import { SendMessage } from '../../../core/src/lib/interface/conversation.interface';
import { ParticipantService } from '../../../core/src/lib/participant.service';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_CONFIG_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  PARTNER_ID_TOKEN,
} from '../../../core/src/lib/tokens';
import { ViewMode } from '../../../core/src/lib/types';
import { ViewModeService } from '../../../core/src/lib/view-mode.service';
import { ViewService } from '../../../core/src/lib/view.service';
import { ConversationUIModule } from '../../../ui/src/conversation-ui.module';
@Component({
  standalone: true,
  selector: 'inbox-container',
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    GalaxyPageModule,
    GalaxySnackbarModule,
    GalaxyPageNavModule,
    GalaxyNavLayoutModule,
    ConversationUIModule,
    BusinessSelectorDialogModule,
  ],
  templateUrl: './inbox-container.component.html',
  styleUrls: ['./inbox-container.component.scss'],
})
export class InboxContainerComponent implements OnInit {
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly participantService = inject(ParticipantService);

  protected readonly showNewMessageButton = !this.inboxConfig.hideNewMessageButton;
  protected readonly learnMoreUrl = this.inboxConfig.learnMoreUrl || '';

  protected readonly showSettings$ = this.inboxService.canAccessEmbeddedSettings$;
  protected readonly showInboxViews$ = this.inboxService.showInboxViews$;
  protected readonly viewMode$ = this.viewModeService.viewMode$;
  protected readonly isModal$ = this.viewMode$.pipe(map((mode) => mode === 'modal'));
  readonly organizationHasConversations$ = this.conversationListService.organizationHasConversations$;

  viewingConversation$ = this.conversationService.currentFirestoreConversationId$.pipe(
    map((conversationId) => !!conversationId),
  );

  private readonly partnerId = toSignal(inject(PARTNER_ID_TOKEN));
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));

  constructor(
    private readonly conversationService: ConversationService,
    private readonly conversationListService: ConversationListService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly snackbar: SnackbarService,
    private readonly analyticsService: ProductAnalyticsService,
    @Inject(CONVERSATION_CONFIG_TOKEN) private readonly inboxConfig: ConversationConfig,
    private readonly inboxService: InboxService,
    private readonly viewModeService: ViewModeService,
    private readonly viewService: ViewService,
  ) {}

  ngOnInit(): void {
    this.showInboxViews$.pipe(
      take(1),
      withLatestFrom(this.viewService.selectedViewId$),
      map(([showInboxViews, selectedViewId]) => {
        if (showInboxViews) {
          this.router.navigate([{ outlets: { inbox: ['inbox', 'views', selectedViewId, 'conversations'] } }], {
            queryParamsHandling: 'preserve',
          });
        }
      }),
    );
  }

  closeModal(): void {
    this.viewModeService.toggleInboxOpen();
  }

  protected toggleView(state: ViewMode): void {
    const isFullView = state === 'sidebar';
    this.analyticsService.trackToggle('inbox-view-mode-toggle-state', isFullView);
    this.viewModeService.toggleViewMode();
  }

  // This modal will need to change if button is shown in any Task Manager or Vendor Center.
  // This modal will need to change if this component is put in Business App.
  // SMBs and Vendors don't have accounts to pick from.
  // Not sure the limitation for Task Manager; I anticipate it is because digital agents can start conversations with partners and accounts.
  newMessage(): void {
    const currentIAMParticipant$ = this.participantService.buildIAMUserParticipant();
    this.dialog
      .open(BusinessSelectorDialogComponent, {
        width: '500px',
        data: {
          partnerId: this.partnerId(),
        },
      })
      .afterClosed()
      .pipe(
        filter((business: AccountGroup) => !!business),
        withLatestFrom(currentIAMParticipant$),
        switchMap(([business, currentParticipant]) => {
          return this.hostAppInterface.buildSendMessageParams(
            currentParticipant,
            [
              new SubjectParticipant({
                internalParticipantId: this.partnerId(),
                participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
              }),
              new SubjectParticipant({
                internalParticipantId: business.accountGroupId,
                participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
              }),
            ],
            ConversationChannel.CONVERSATION_CHANNEL_INTERNAL,
          );
        }),
        switchMap((sendMessage: SendMessage) =>
          this.conversationService.createConversation(
            sendMessage.participants ?? [],
            sendMessage.channel,
            sendMessage.location,
          ),
        ),
        tap((conversationDetails) => {
          if (!conversationDetails?.conversation) return;
          this.analyticsService.trackEvent('inbox', 'send-message', 'create-conversation-success', 0, {
            accountGroupId: this.accountGroupId(),
            partnerId: this.partnerId(),
            location: this.inboxService.platformLocation,
            channel: this.conversationService.getConversationChannel(),
          });
        }),
        switchMap((conversationResp) =>
          this.conversationService.getConversationDetail(conversationResp.conversation.conversationId),
        ),
        map((conversationDetails) => toFirestoreId(conversationDetails?.conversation?.conversationId)),
      )
      .subscribe({
        next: (conversationId) =>
          this.router.navigate(['conversations', conversationId], {
            relativeTo: this.route,
          }),
        error: (err) => {
          console.warn('Error to create a conversation: ', err);
          this.snackbar.openErrorSnack(err);
          this.analyticsService.trackEvent('inbox', 'send-message', 'create-conversation-error', 0, {
            accountGroupId: this.accountGroupId(),
            partnerId: this.partnerId(),
            location: this.inboxService.platformLocation,
            channel: this.conversationService.getConversationChannel(),
            message: err?.error?.message,
          });
        },
      });
  }
}
